import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container,
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { useTranslation } from 'react-i18next';

import { getStagingPickListThunk } from '../../../../../../../../store/stagedShipmentPickList/thunks';
import { useAppDispatch, useAppSelector } from '../../../../../../../../store';
import { MoveStagedAliquotInput, PickListAliquot } from '../../../../../../../../core/api/lab.types';
import {
  shipmentPickListIsBusySelector,
  shipmentPickListResponseSelector,
} from '../../../../../../../../store/stagedShipmentPickList';
import {
  moveStagedAliquotReset,
  moveStagedAliquotThunk,
} from '../../../../../../../../store/stagedShipmentMoveAliquot';
import { ButtonRow, Loader } from '@omnigenbiodata/ui';
import {
  moveStagedAliquoteHasErrorSelector,
  moveStagedAliquoteIsBusySelector,
  moveStagedAliquoteResponseSelector,
} from '../../../../../../../../store/stagedShipmentMoveAliquot/selectors';
import StatusMessage from '../../../../../../../../components/StatusMessage';
interface PickListModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PickListModal = ({ isOpen, onClose }: PickListModalProps) => {
  const { t } = useTranslation('portal');
  const dispatch = useAppDispatch();
  const picklist = useAppSelector(shipmentPickListResponseSelector);
  const [addedSuccess, setAddedSuccess] = useState<any>({ showMsg: false, newBox: '', newPosition: '', sampleId: '' });
  const [records, setRecords] = useState<PickListAliquot[]>([]);
  const getStagingPickListBusy = useAppSelector(shipmentPickListIsBusySelector);
  const moveStagedAliquoteIsBusy = useAppSelector(moveStagedAliquoteIsBusySelector);
  const moveStagedAliquoteIsError = useAppSelector(moveStagedAliquoteHasErrorSelector);
  const moveStagedAliquote = useAppSelector(moveStagedAliquoteResponseSelector);
  const [confirmSampleIdErrors, setConfirmSampleIdErrors] = useState<Record<string, string>>({});
  const [newBoxBarcodeErrors, setNewBoxBarcodeErrors] = useState<Record<string, string>>({});

  const groupedRecords = useMemo(() => {
    return records.reduce<
      Record<
        string,
        {
          freezer: string;
          cryobox: string;
          freezerShelfNo: number;
          freezerShelfRackNo: number;
          freezerRackPositionNo: number;
          records: PickListAliquot[];
        }
      >
    >((acc, record) => {
      const key = `${record.freezerName}__${record.cryoboxSerialNo}`;

      if (!acc[key]) {
        acc[key] = {
          freezer: record.freezerName,
          cryobox: record.cryoboxSerialNo,
          freezerShelfNo: record.freezerShelfNo,
          freezerShelfRackNo: record.freezerShelfRackNo,
          freezerRackPositionNo: record.freezerRackPositionNo,
          records: [],
        };
      }

      acc[key].records.push(record);

      return acc;
    }, {});
  }, [records]);

  useEffect(() => {
    return () => {
      dispatch(moveStagedAliquotReset());
    };
  }, [dispatch]);

  useEffect(() => {
    if (isOpen) {
      dispatch(getStagingPickListThunk());
    }
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (!picklist) return;

    setRecords((currentRecords) => {
      return picklist.map((item) => {
        const existingRecord = currentRecords.find((record) => record.aliquotUuid === item.aliquotUuid);

        return {
          ...item,
          confirmSampleId: existingRecord?.confirmSampleId ?? item.confirmSampleId,
          newBoxBarcode: existingRecord?.newBoxBarcode ?? item.newBoxBarcode,
          newSamplePosition: existingRecord?.newSamplePosition ?? item.newSamplePosition,
        };
      });
    });
  }, [picklist]);

  useEffect(() => {
    if (!isOpen || !moveStagedAliquote?.aliquotUuid) return;

    dispatch(getStagingPickListThunk());

    setAddedSuccess((prev: any) => ({
      ...prev,
      showMsg: true,
    }));
  }, [dispatch, isOpen, moveStagedAliquote]);

  useEffect(() => {
    if (!addedSuccess.showMsg) return;

    const timer = setTimeout(() => {
      setAddedSuccess((prev: any) => ({
        ...prev,
        showMsg: false,
      }));
    }, 10000);

    return () => clearTimeout(timer);
  }, [addedSuccess.showMsg]);

  const handleChange = (aliquotUuid: string, field: keyof PickListAliquot, value: string | number) => {
    setRecords((prev) =>
      prev.map((item) =>
        item.aliquotUuid === aliquotUuid
          ? {
              ...item,
              [field]: value,
            }
          : item,
      ),
    );

    if (field === 'confirmSampleId') {
      const record = records.find((item) => item.aliquotUuid === aliquotUuid);
      const stringValue = String(value);

      if (!/^[a-zA-Z0-9-]+$/.test(stringValue)) {
        setConfirmSampleIdErrors((prev) => ({
          ...prev,
          [aliquotUuid]: 'Only letters and numbers are allowed.',
        }));
      } else if (record && value !== record.aliquotBarcode) {
        setConfirmSampleIdErrors((prev) => ({
          ...prev,
          [aliquotUuid]: "Sample ID doesn't match.",
        }));
      } else {
        setConfirmSampleIdErrors((prev) => {
          const updated = { ...prev };
          delete updated[aliquotUuid];
          return updated;
        });
      }
    }

    if (field === 'newBoxBarcode') {
      if (!/^[a-zA-Z0-9-]+$/.test(String(value))) {
        setNewBoxBarcodeErrors((prev) => ({
          ...prev,
          [aliquotUuid]: 'Only letters, numbers and hyphens are allowed.',
        }));
      } else {
        setNewBoxBarcodeErrors((prev) => {
          const updated = { ...prev };
          delete updated[aliquotUuid];
          return updated;
        });
      }
    }
  };

  const handleMoved = async (record: PickListAliquot) => {
    if (record.confirmSampleId !== record.aliquotBarcode) {
      setConfirmSampleIdErrors((prev) => ({
        ...prev,
        [record.aliquotUuid]: "Sample ID doesn't match.",
      }));
      return;
    }

    const values: MoveStagedAliquotInput = {
      aliquotUuid: record.aliquotUuid,
      updatedCryoboxBarcode: record.newBoxBarcode || '',
      updatedCryoboxPosition: record.newSamplePosition || null,
    };

    setAddedSuccess({
      showMsg: false,
      sampleId: record.aliquotBarcode,
      newBox: record.newBoxBarcode,
      newPosition: record.newSamplePosition,
    });

    if (isOpen) {
      await dispatch(moveStagedAliquotThunk(values));
    }
  };

  return (
    <Dialog open={isOpen} maxWidth="lg" fullWidth onClose={onClose}>
      <DialogTitle></DialogTitle>

      <DialogContent>
        <Typography variant="h5" fontWeight={700}>
          Samples to remove
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This list shows samples that require removal from the selected cryoboxes before the shipment can be
          manifested. Review each item and confirm the required sample movement.
        </Typography>
        <Container maxWidth="sm" sx={{ paddingX: 0, paddingY: 2 }}>
          <StatusMessage
            duration={25000}
            severity="error"
            show={moveStagedAliquoteIsError.hasError}
            message={moveStagedAliquoteIsError.message}
            onClose={() => {
              dispatch(moveStagedAliquotReset());
            }}
          />

          <StatusMessage
            duration={25000}
            severity="success"
            show={addedSuccess && addedSuccess.showMsg}
            message={`Sample (${addedSuccess.sampleId}) has been moved to box (${addedSuccess.newBox}) position (
                ${addedSuccess.newPosition}) successfully.`}
            onClose={() => {
              dispatch(moveStagedAliquotReset());
            }}
          />
        </Container>
        {records.length === 0 && !getStagingPickListBusy && (
          <Box
            sx={{
              py: 10,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h5" fontWeight={600}>
              There are no more samples to remove.
            </Typography>
          </Box>
        )}

        {Object.values(groupedRecords).map((group) => {
          const freezerRecords = group.records;

          return (
            <>
              <Paper
                key={`${group.freezer}-${group.cryobox}`}
                elevation={2}
                sx={{
                  mb: 3,
                  overflow: 'hidden',
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    borderTop: '4px solid #0f3d4c',
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: '#f4f4f4',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Inventory2OutlinedIcon />
                    </Box>

                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {group.freezer} / {group.cryobox}
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        Shelf {group.freezerShelfNo} / Rack {group.freezerShelfRackNo} / Position{' '}
                        {group.freezerRackPositionNo}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Sample ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">
                          Sample Position
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} width={190} align="center">
                          Confirm Sample ID
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }} width={190} align="center">
                          New Box Barcode
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }} width={190} align="center">
                          New Sample Position (e.g. 1 - 81)
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {freezerRecords.map((record) => {
                        const isFirstRow = record.aliquotUuid === freezerRecords[0]?.aliquotUuid;

                        return (
                          <TableRow
                            className="cellSampleRow"
                            key={record.aliquotUuid}
                            data-testid={`cellSampleRow_${record.aliquotUuid}`}
                          >
                            <TableCell data-testid="sampleBarcode">{record.aliquotBarcode}</TableCell>

                            <TableCell data-testid="cellSamplePosition" align="center">
                              {record.aliquotPosition}
                            </TableCell>

                            <TableCell data-testid="cellSampleReason">{record.reason}</TableCell>

                            <TableCell data-testid="cellConfirmSampleId">
                              <TextField
                                fullWidth
                                disabled={!isFirstRow}
                                size="small"
                                value={record.confirmSampleId ?? ''}
                                error={Boolean(confirmSampleIdErrors[record.aliquotUuid])}
                                helperText={confirmSampleIdErrors[record.aliquotUuid] ?? ''}
                                onChange={(e) => handleChange(record.aliquotUuid, 'confirmSampleId', e.target.value)}
                              />
                            </TableCell>

                            <TableCell data-testid="cellNewBoxBarcode">
                              <TextField
                                fullWidth
                                disabled={!isFirstRow}
                                size="small"
                                value={record.newBoxBarcode ?? ''}
                                error={Boolean(newBoxBarcodeErrors[record.aliquotUuid])}
                                helperText={newBoxBarcodeErrors[record.aliquotUuid] ?? ''}
                                onChange={(e) => handleChange(record.aliquotUuid, 'newBoxBarcode', e.target.value)}
                              />
                            </TableCell>

                            <TableCell data-testid="cellNewSamplePosition">
                              <TextField
                                type="number"
                                fullWidth
                                disabled={!isFirstRow}
                                size="small"
                                value={record.newSamplePosition ?? ''}
                                error={!!record.newSamplePosition && isNaN(Number(record.newSamplePosition))}
                                helperText={
                                  !!record.newSamplePosition && isNaN(Number(record.newSamplePosition))
                                    ? 'Position must be a number.'
                                    : ''
                                }
                                onChange={(e) => handleChange(record.aliquotUuid, 'newSamplePosition', e.target.value)}
                              />
                            </TableCell>

                            <TableCell align="right">
                              <Button
                                variant="contained"
                                data-testid="btnMoveSample"
                                disabled={
                                  record.newBoxBarcode === undefined ||
                                  record.newBoxBarcode === '' ||
                                  record.newSamplePosition === null ||
                                  record.newSamplePosition === undefined ||
                                  record.newSamplePosition.toString() === '' ||
                                  record.confirmSampleId === undefined ||
                                  record.confirmSampleId === '' ||
                                  Boolean(confirmSampleIdErrors[record.aliquotUuid]) ||
                                  Boolean(newBoxBarcodeErrors[record.aliquotUuid]) ||
                                  isNaN(Number(record.newSamplePosition))
                                }
                                onClick={() => handleMoved(record)}
                              >
                                Move
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          );
        })}
        <Loader data-testid="loading-message" isVisible={getStagingPickListBusy} label={t(`Loading...`) as string} />
        <Loader
          data-testid="moving-message"
          isVisible={moveStagedAliquoteIsBusy}
          label={t(`Moving aliquit...`) as string}
        />
      </DialogContent>

      <Divider />

      <DialogActions>
        <Box px={2} pb={2} width="100%">
          <ButtonRow
            buttonSize="large"
            backLabel={t('buttons.close') as string}
            showForward={false}
            onBack={onClose}
            forwardColor="primary"
            backIcon={null}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PickListModal;
